import { useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import HeroDetail from "../../components/catalog/hero/HeroDetail/HeroDetail";
import SectionAudioPlayer from "../../components/catalog/sections/SectionAudioPlayer/SectionAudioPlayer";
import SectionBreadcumb from "../../components/catalog/sections/SectionBreadcumb/SectionBreadcumb";
import SectionRecommended from "../../components/catalog/sections/SectionRecommended/SectionRecommended";
import SectionReview from "../../components/catalog/sections/SectionReview/SectionReview";
import SectionTestimonyComment from "../../components/catalog/sections/SectionTestimonyComment/SectionTestimonyComment";
import Layout from "../../components/global/Layout";
import { BASE_URL } from "../../utils/config";
import useSWR from 'swr';

const fetcher = (url) => fetch(url).then((res) => res.json());

const STORAGE_KEY = "books"; // Sama dengan Catalog.js
const STORAGE_TIMESTAMP_KEY = "books_timestamp"; // Sama dengan Catalog.js
const EXPIRY_HOURS = 6; // Cache expire setiap 6 jam

function useBookDetail(slug) {
  // URL detail bisa berisi slug lama atau id numerik (hasil MiniSearch)
  const isBookId = /^\d+$/.test(String(slug || ''));

  // Cek apakah cache masih valid dan cari buku dari localStorage
  const { cachedBook, isCacheValid } = useMemo(() => {
    try {
      const lastSavedTime = localStorage.getItem(STORAGE_TIMESTAMP_KEY);
      const booksData = localStorage.getItem(STORAGE_KEY);
      
      if (!lastSavedTime || !booksData) {
        console.log(`📖 No cache found for books`);
        return { cachedBook: null, isCacheValid: false };
      }
      
      // Cek apakah cache sudah expired
      const currentTime = new Date().getTime();
      const savedTime = parseInt(lastSavedTime);
      const expiryTime = EXPIRY_HOURS * 60 * 60 * 1000; // 6 jam dalam milidetik
      const isValid = (currentTime - savedTime) < expiryTime;
      
      if (!isValid) {
        console.log(`⏰ Cache expired for books (age: ${Math.floor((currentTime - savedTime) / 1000 / 60 / 60)} hours)`);
        return { cachedBook: null, isCacheValid: false };
      }
      
      // Cari buku berdasarkan id (hasil MiniSearch) atau slug (URL lama)
      const books = JSON.parse(booksData);
      const foundBook = isBookId
        ? books.find((book) => String(book.id) === String(slug))
        : books.find((book) => book.slug === slug);
      
      if (foundBook) {
        console.log(`✅ Book "${slug}" found in cache`, {
          cacheAge: `${Math.floor((currentTime - savedTime) / 1000 / 60 / 60)} hours`,
          savedAt: new Date(savedTime).toISOString()
        });
      } else {
        console.log(`📖 Book "${slug}" not found in cache, will fetch from API`);
      }
      
      return { 
        cachedBook: foundBook || null, 
        isCacheValid: isValid 
      };
    } catch (error) {
      console.error("❌ Error reading book cache:", error);
      return { cachedBook: null, isCacheValid: false };
    }
  }, [slug]);
  
  // Fetch data jika tidak ada di cache atau cache expired
  const shouldFetch = !cachedBook;
  const detailUrl = isBookId
    ? `${BASE_URL}/api/catalogue/getDetails?id=${slug}`
    : `${BASE_URL}/api/catalogue/getDetails?slug=${slug}`;

  const { data, error, isValidating } = useSWR(
    shouldFetch ? detailUrl : null,
    fetcher,
    {
      refreshInterval: 21600000, // Re-fetch setiap 6 Jam
      dedupingInterval: 21600000, // Cegah fetching ulang dalam 6 Jam
      revalidateOnFocus: false, // Jangan revalidate saat window fokus kembali
      revalidateOnReconnect: false
    }
  );

  // Gunakan data dari cache jika valid, jika tidak gunakan hasil fetch dari API
  const book = cachedBook ?? data?.results ?? [];
  const loadingBook = !cachedBook && isValidating;

  // Simpan hasil fetch ke localStorage setelah mendapatkan data dari API
  useEffect(() => {
    if (data?.results) {
      try {
        const booksData = localStorage.getItem(STORAGE_KEY);
        const books = booksData ? JSON.parse(booksData) : [];
        
        // Update atau tambahkan buku baru (cocokkan lewat id, fallback slug)
        const fetchedBook = data.results;
        const bookIndex = books.findIndex((b) =>
          b?.id && fetchedBook?.id
            ? String(b.id) === String(fetchedBook.id)
            : b.slug === slug
        );
        if (bookIndex >= 0) {
          books[bookIndex] = data.results; // Update existing
        } else {
          books.push(data.results); // Add new
        }
        
        localStorage.setItem(STORAGE_KEY, JSON.stringify(books));
        
        // Set timestamp untuk cache expiry
        const currentTime = new Date().getTime();
        localStorage.setItem(STORAGE_TIMESTAMP_KEY, currentTime.toString());
        
        console.log(`✅ Book detail "${slug}" saved to cache`, {
          totalBooksInCache: books.length,
          action: bookIndex >= 0 ? 'updated' : 'added',
          timestamp: new Date(currentTime).toISOString()
        });
      } catch (error) {
        console.error("❌ Error saving book to cache:", error);
      }
    }
  }, [data, slug]);

  return { book, errorBook: error, loadingBook };
}


function useReviews(slug) {
  const { data, error, isValidating } = useSWR(`${BASE_URL}/api/review/getReviews?slug=${slug}&limit=5&offset=0`, fetcher, {
      refreshInterval: 21600000,    // Re-fetch setiap 6 Jam
      dedupingInterval: 21600000,   // Cegah fetching ulang dalam 6 Jam
      revalidateOnFocus: false,  // Jangan revalidate saat window fokus kembali
      revalidateOnReconnect: false
  });

  const reviews = data?.results ?? [];
  const loadingReviews = isValidating ?? false;

  return { reviews, errorReviews: error ,loadingReviews };
}

function useRecommendBooks(slug) {
  const { data, error, isValidating } = useSWR(`${BASE_URL}/api/catalogue/getRecommendCatalogue?slug=${slug}&qty=8`, fetcher, {
      refreshInterval: 21600000,    // Re-fetch setiap 6 Jam
      dedupingInterval: 21600000,   // Cegah fetching ulang dalam 6 Jam
      revalidateOnFocus: false,  // Jangan revalidate saat window fokus kembali
      revalidateOnReconnect: false
  });

  const recommendBooks = data?.results ?? [];
  const loadingRecommendBooks = isValidating ?? false;

  return { recommendBooks, errorRecommendBooks: error ,loadingRecommendBooks };
}

const CatalogDetail = () => {
  const token = localStorage.getItem("user_token");
  const { slug } = useParams();
  
  const { book, loadingBook } = useBookDetail(slug);
  // Detail bisa dibuka lewat id (hasil MiniSearch) maupun slug lama.
  // Setelah data didapat, pakai slug asli untuk review & rekomendasi.
  const resolvedSlug = book?.slug || slug;
  const { recommendBooks, loadingRecommendBooks } = useRecommendBooks(resolvedSlug);
  const { reviews } = useReviews(resolvedSlug);

  return (
    <Layout>
      {book?.length !== 0 || loadingBook ? (
        <>
          <SectionBreadcumb
            category={book?.category}
            level={book?.level}
            title={book?.title}
            slug={book?.slug}
            code={book?.code}
          />
          <HeroDetail
            id={book?.id}
            token={token}
            slug={book?.slug}
            image={book?.image}
            bookType={book?.type}
            title={book?.title}
            het_id={book?.het_id}
            publisher={book?.publisher}
            isbn={book?.isbn}
            edition={book?.edition}
            writer={book?.writer}
            attachment={book?.attachment}
            totalDownload={book?.total_download}
            totalRead={book?.total_read}
            totalPlay={book?.total_play}
            loading={loadingBook}
            category={book?.category}
            price_zone_1={book?.price_zone_1}
            price_zone_2={book?.price_zone_2}
            price_zone_3={book?.price_zone_3}
            price_zone_4={book?.price_zone_4}
            price_zone_5A={book?.price_zone_5A}
            price_zone_5B={book?.price_zone_5B}
          />
          {book?.type === "audio" && (
            <SectionAudioPlayer audios={book?.audio_attachment} />
          )}
          <SectionReview token={token} slug={book?.slug} />
          <SectionTestimonyComment reviews={reviews} />
          {recommendBooks.length !== 0 && (
            <SectionRecommended
              recommendBooks={recommendBooks}
              loading={loadingRecommendBooks}
            />
          )}
        </>
      ) : (
        <div className="text-center my-5">
          <img
            width="60"
            src="/assets/image/catalog/not-found.png"
            className="img-fluid"
            alt="Not found"
          />
          <div className="text-center mt-2">
            Buku tidak ditemukan / dalam revisi
          </div>
        </div>
      )}
    </Layout>
  );
};

export default CatalogDetail;
